import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { NotebookIcon as Notebook, FloppyDiskIcon as FloppyDisk, ArrowLeftIcon as ArrowLeft } from "@phosphor-icons/react";

/**
 * Admin → Journal settings.
 *
 * One field today: the standing site-wide footer disclaimer that
 * renders at the bottom of every published post and on the /journal
 * index. Pulled in by the public PublicShell-wrapped pages via
 * GET /api/journal/settings. Admin writes via PUT /api/admin/journal/settings.
 *
 * Why this lives on its own page: legal/comms typically owns the
 * wording, and decoupling it from the editor means the wording stays
 * stable across all posts even when individual authors aren't paying
 * attention. Per-post one-off notices live on the post itself.
 */
const SUGGESTED = [
  {
    label: "General editorial",
    text:
      "Editorial views are the author's and reflect Armstrong K9 Registry " +
      "policies at time of publication. This is general information, not " +
      "veterinary, legal, or tax advice — consult a licensed professional " +
      "for decisions about your dog or your business.",
  },
  {
    label: "Veterinary / health emphasis",
    text:
      "Content here is educational, not veterinary advice. Talk to a " +
      "licensed veterinarian before making decisions about your dog's " +
      "health, breeding, nutrition, or care.",
  },
  {
    label: "Legal / commerce emphasis",
    text:
      "General information only — not legal, tax, or financial advice. " +
      "Breeder contracts, transport regulations, sales tax, and Stripe " +
      "payouts vary by jurisdiction; consult counsel licensed in your state.",
  },
];

export default function AdminJournalSettings() {
  const [value, setValue] = useState("");
  const [original, setOriginal] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/journal/settings")
      .then((r) => {
        const v = r.data?.footer_disclaimer || "";
        setValue(v);
        setOriginal(v);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/journal/settings", {
        footer_disclaimer: value,
      });
      const v = data?.footer_disclaimer || "";
      setValue(v);
      setOriginal(v);
      toast.success("Footer disclaimer updated.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const dirty = value !== original;

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pb-16" data-testid="admin-journal-settings">
        <header className="mb-6">
          <Link to="/admin/journal" className="inline-flex items-center gap-1 text-[12px] text-[#6B6B6B] hover:text-ink mb-2">
            <ArrowLeft size={12} weight="bold" /> Back to Journal
          </Link>
          <h1 className="font-spectral text-2xl sm:text-3xl tracking-tight flex items-center gap-2">
            <Notebook size={26} weight="duotone" /> Journal settings
          </h1>
          <p className="text-[13px] text-[#6B6B6B] mt-1 max-w-2xl">
            Standing footer disclaimer — auto-rendered at the bottom of
            every published post and the <Link to="/journal" className="underline">/journal</Link>{" "}
            index. Edit the wording any time; changes apply immediately
            to every public post without a redeploy.
          </p>
        </header>

        {loading ? (
          <div className="text-[#6B6B6B] text-sm">Loading…</div>
        ) : (
          <>
            <section className="rounded-2xl border border-[#EAEAEA] bg-white p-5 mb-6">
              <Label className="text-xs">Footer disclaimer</Label>
              <Textarea
                value={value}
                onChange={(e) => setValue(e.target.value)}
                rows={6}
                className="text-[13px] mt-1"
                data-testid="journal-settings-footer-disclaimer"
                placeholder="One paragraph of editorial / legal / health framing that should appear on every post."
              />
              <p className="text-[10.5px] text-[#9A9A9A] mt-2">
                Plain text. Renders in italic gray below the article body, after the share strip. Max 2000 characters.
              </p>
            </section>

            <section className="mb-8" data-testid="journal-settings-suggestions">
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#6B6B6B] mb-2">
                Suggested wording
              </p>
              <div className="space-y-2">
                {SUGGESTED.map((s) => (
                  <button
                    key={s.label}
                    type="button"
                    onClick={() => setValue(s.text)}
                    className="block w-full text-left rounded-xl border border-[#EAEAEA] hover:border-[#C9A227]/60 bg-white px-4 py-3 transition-colors"
                  >
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold-dark mb-1">
                      {s.label}
                    </p>
                    <p className="text-[12.5px] italic text-[#3a3a3a] leading-relaxed">{s.text}</p>
                  </button>
                ))}
              </div>
              <p className="text-[10.5px] text-[#9A9A9A] mt-2">
                These are starting points — run the final wording past your counsel before relying on it in production.
              </p>
            </section>

            <div className="flex items-center gap-3">
              <Button
                onClick={save}
                disabled={!dirty || saving}
                className="rounded-full bg-ink hover:bg-crimson text-white"
                data-testid="journal-settings-save"
              >
                <FloppyDisk size={14} weight="bold" className="mr-1" />
                {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
              </Button>
              {dirty && (
                <button
                  type="button"
                  onClick={() => setValue(original)}
                  className="text-[12px] text-[#6B6B6B] hover:text-ink underline underline-offset-2"
                >
                  Revert
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
